import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, FormControl, Grid, IconButton, InputLabel, LinearProgress, Link, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, Info as InfoIcon, Build as SreIcon
} from '@mui/icons-material';
import { authGet } from '../../services/apiService';
import VitalSignsQueryLink from './VitalSignsQueryLink';
import { L1_ACCENT, MAJOR_INCIDENT_ACCENT, MAJOR_INCIDENT_BG, MAJOR_INCIDENT_BG_HOVER } from './colorTokens';
import OpenAgeCard, { EMPTY_AGE_COUNTS } from './OpenAgeCard';
import { customerNames } from './ticketListHelpers';

const SRE_COLOR = L1_ACCENT;
const COLUMNS = [
  { field: 'id', label: 'ServiceNow #' },
  { field: 'type', label: 'Type' },
  { field: 'title', label: 'Description' },
  { field: 'state', label: 'State' },
  { field: 'priority', label: 'Priority' },
  { field: 'cases', label: 'SF Case(s)' },
  { field: 'customer', label: 'Customer' },
  { field: 'assignedTo', label: 'Assigned to' },
  { field: 'age', label: 'Age (days)', align: 'right' }
];
const SHOW_OPTIONS = [
  ['open', 'Open tickets'],
  ['closed', 'Closed tickets'],
  ['notFound', 'Not found in ServiceNow'],
  ['all', 'All tickets']
];
const NOT_FOUND_HINT = 'Not found in ServiceNow. Check the ticket number on the Salesforce case.';
// Major incident rows: a peach tint with an orange left edge, so they stand out from the plain rows.
const MAJOR_BG = MAJOR_INCIDENT_BG;
const MAJOR_BG_HOVER = MAJOR_INCIDENT_BG_HOVER;
const MAJOR_ACCENT = MAJOR_INCIDENT_ACCENT;
const TYPE_LABEL = { Incident: 'Incident', Service: 'Request' };

const ageColor = (age) => {
  if (age == null) return 'default';
  if (age > 180) return '#d32f2f';
  if (age > 90) return '#f57c00';
  if (age > 30) return '#fbc02d';
  return '#388e3c';
};

const sortValue = (ticket, field) => {
  if (field === 'customer') return customerNames(ticket);
  if (field === 'cases') return ticket.cases.map((c) => c.id).join(',');
  return ticket[field];
};

const INFO = 'ServiceNow tickets (incidents, requests and request items) that open Salesforce L1 cases link to. '
  + 'Open counts the tickets whose ServiceNow state is not Closed, Closed Complete, Resolved or Cancelled; a ticket '
  + 'with no state yet counts as open. Tickets that ServiceNow has no record of (usually a mistyped number on the '
  + 'Salesforce case) are not counted; the list shows them as Not found. Each ticket is counted once, even if '
  + 'several cases link to it. The list can also show the closed tickets that open cases still link to. Open major '
  + 'incidents (accepted in ServiceNow) are always included and shaded, even when no Salesforce case links to them. '
  + 'Same number as the SRE tile.';

const SummaryCard = ({ open, major, closed, notFound, rows }) => (
  <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, pl: 2, pr: 0.5, py: 0.5,
      background: `linear-gradient(135deg, ${SRE_COLOR}ee 0%, ${SRE_COLOR}bb 100%)`
    }}>
      <SreIcon sx={{ color: '#fff' }} />
      <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 0.3, flexGrow: 1 }}>
        SRE Open
      </Typography>
      <VitalSignsQueryLink tile="sreOpen" />
      <Tooltip title={INFO} arrow placement="top">
        <IconButton size="small" aria-label="About SRE Open"><InfoIcon fontSize="small" sx={{ color: '#fff' }} /></IconButton>
      </Tooltip>
    </Box>
    <Box sx={{ p: 1.75 }}>
      <Box sx={{ textAlign: 'center', pb: 1.25 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: SRE_COLOR, lineHeight: 1 }}>{open}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Open</Typography>
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary' }}>
        OPEN BY STATE
      </Typography>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mt: 0.5 }}>
        {rows.map(({ state, count }) => (
          <Box key={state} sx={{ display: 'flex', px: 1.5, py: 0.4, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ flex: 1 }}>{state}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: SRE_COLOR }}>{count}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', px: 1.5, py: 0.4, bgcolor: '#f5f5f5' }}>
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 700 }}>Total open</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: SRE_COLOR }}>{open}</Typography>
        </Box>
      </Box>
      {major > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 600, color: MAJOR_ACCENT }}>
          {major} open {major === 1 ? 'ticket is a major incident' : 'tickets are major incidents'}.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {closed} closed {closed === 1 ? 'ticket is' : 'tickets are'} still linked to open L1 cases.
      </Typography>
      {notFound > 0 && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
          {notFound} {notFound === 1 ? 'ticket was' : 'tickets were'} not found in ServiceNow and {notFound === 1 ? 'is' : 'are'} not counted.
        </Typography>
      )}
    </Box>
  </Paper>
);

/** ServiceNow tickets that open Salesforce (L1) cases link to, with the cases that link each one. */
const SreOpenView = () => {
  const [tickets, setTickets] = useState(null);
  const [summary, setSummary] = useState({
    open: 0, major: 0, closed: 0, notFound: 0, total: 0, byState: [], byAge: EMPTY_AGE_COUNTS, oldestAge: null
  });
  const [error, setError] = useState(null);
  const [show, setShow] = useState('open');
  const [selectedState, setSelectedState] = useState('');
  const [ageBucket, setAgeBucket] = useState('');
  const [sortField, setSortField] = useState('age');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    let cancelled = false;
    authGet('/api/customer-issues/vital-signs-sre-open')
      .then((response) => {
        if (cancelled) return;
        if (!response.success) throw new Error(response.message || 'Failed to load ServiceNow tickets');
        setTickets(response.tickets || []);
        if (response.summary) setSummary(response.summary);
      })
      .catch((err) => {
        console.error('Error fetching SRE ServiceNow tickets:', err);
        if (!cancelled) setError('Failed to load ServiceNow tickets. Please try again later.');
      });
    return () => { cancelled = true; };
  }, []);

  const all = useMemo(() => tickets || [], [tickets]);
  const inShow = useMemo(
    () => all.filter((ticket) => {
      if (show === 'open') return ticket.open;
      if (show === 'closed') return !ticket.open && !ticket.notFound;
      if (show === 'notFound') return ticket.notFound;
      return true;
    }),
    [all, show]
  );
  const states = useMemo(
    () => [...new Set(inShow.map((ticket) => ticket.state || 'Unknown'))].sort((a, b) => a.localeCompare(b)),
    [inShow]
  );

  const rows = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return inShow
      .filter((ticket) => !selectedState || (ticket.state || 'Unknown') === selectedState)
      .filter((ticket) => !ageBucket || (ticket.open && ticket.ageBucket === ageBucket))
      .sort((a, b) => {
        const va = sortValue(a, sortField);
        const vb = sortValue(b, sortField);
        if (sortField === 'age') {
          // A ticket with no opened date always sorts last, whichever way the list is sorted.
          const aMissing = va == null;
          const bMissing = vb == null;
          if (aMissing || bMissing) return Number(aMissing) - Number(bMissing);
          return dir * (va - vb);
        }
        return dir * String(va || '').toLowerCase().localeCompare(String(vb || '').toLowerCase(), undefined, { numeric: true });
      });
  }, [inShow, selectedState, ageBucket, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDirection('asc'); }
  };

  if (!tickets && !error) return <LinearProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SummaryCard open={summary.open} major={summary.major || 0} closed={summary.closed} notFound={summary.notFound} rows={summary.byState} />
        </Grid>
        <Grid item xs={12} md={8}>
          <OpenAgeCard
            counts={summary.byAge || EMPTY_AGE_COUNTS}
            oldestDays={summary.oldestAge}
            selectedBucket={ageBucket}
            onSelectBucket={(bucket) => { setAgeBucket(bucket); setShow('open'); }}
          />
        </Grid>
      </Grid>

      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>ServiceNow tickets linked to L1 cases</Typography>
          <Typography variant="caption" color="text.secondary">
            Incidents, requests and request items that an open Salesforce case links to, and open major incidents.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Show</InputLabel>
            <Select
              value={show}
              label="Show"
              onChange={(e) => { setShow(e.target.value); setSelectedState(''); setAgeBucket(''); }}
            >
              {SHOW_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>State</InputLabel>
            <Select value={selectedState} label="State" onChange={(e) => setSelectedState(e.target.value)}>
              <MenuItem value="">All States</MenuItem>
              {states.map((state) => <MenuItem key={state} value={state}>{state}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {rows.length} of {all.length} tickets
            </Typography>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.field}
                    align={col.align}
                    sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort(col.field)}
                  >
                    <Box display="flex" alignItems="center" justifyContent={col.align === 'right' ? 'flex-end' : 'flex-start'}>
                      {col.label}
                      {sortField === col.field && (sortDirection === 'asc'
                        ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />)}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {all.length === 0 ? 'No ServiceNow tickets are linked to open L1 cases.' : 'No tickets found matching the selected filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((ticket) => (
                <TableRow
                  key={ticket.id}
                  hover
                  // Colored per cell, not on the row: the theme's zebra stripe (.MuiTableRow-root:nth-of-type(even))
                  // out-ranks a row-level background, so the row would lose its color whenever it sorts to an even spot.
                  sx={ticket.major ? {
                    '& > td': { bgcolor: MAJOR_BG },
                    '& > td:first-of-type': { borderLeft: `4px solid ${MAJOR_ACCENT}` },
                    '&:hover > td': { bgcolor: MAJOR_BG_HOVER }
                  } : undefined}
                >
                  <TableCell>
                    {ticket.url ? (
                      <Link href={ticket.url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                        {ticket.id}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{ticket.id}</Typography>
                    )}
                    {ticket.major && (
                      <Typography variant="caption" sx={{ display: 'block', color: MAJOR_ACCENT, fontWeight: 600 }}>
                        Major Incident
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{TYPE_LABEL[ticket.type] || ticket.type}</Typography></TableCell>
                  <TableCell sx={{ maxWidth: 400 }}>
                    <Typography
                      variant="body2"
                      color={ticket.notFound ? 'warning.main' : 'text.primary'}
                      sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                      {ticket.notFound ? NOT_FOUND_HINT : (ticket.title || 'No description')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.state || 'Unknown'}
                      size="small"
                      variant="outlined"
                      color={ticket.notFound ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{ticket.priority ? <Chip label={ticket.priority} size="small" variant="outlined" /> : '-'}</TableCell>
                  <TableCell>
                    {ticket.cases.map((c, index) => (
                      <span key={c.id}>
                        {index > 0 && ', '}
                        {c.url
                          ? <Link href={c.url} target="_blank" rel="noopener noreferrer" underline="hover">{c.id}</Link>
                          : c.id}
                      </span>
                    ))}
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{customerNames(ticket) || (ticket.cases.length ? 'Unknown' : '-')}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{ticket.assignedTo || 'Unassigned'}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title={ticket.createdDate ? new Date(ticket.createdDate).toLocaleString() : 'Opened date unknown'}>
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        {ticket.age != null
                          ? <Chip label={`${ticket.age} days`} size="small" sx={{ backgroundColor: ageColor(ticket.age), color: 'white', fontWeight: 'bold' }} />
                          : <Typography variant="body2" color="text.secondary">-</Typography>}
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default SreOpenView;
